import type { NavigationMapInfo, RobotPose, WaypointMap } from '@shared/lib/api/navigation';
import { worldToPx } from '@shared/lib/navigation-utils';

interface RobotMinimapProps {
  mapInfo: NavigationMapInfo;
  mapUrl: string;
  robotPose: RobotPose | null;
  path: Array<[number, number]>;
  waypoints: WaypointMap;
  onClick: () => void;
}

const VISIBLE_METERS = 8;
const CONTAINER_PX = 280;
const DOT_R = 8;

export function RobotMinimap({ mapInfo, mapUrl, robotPose, path, waypoints, onClick }: RobotMinimapProps) {
  const scale = (CONTAINER_PX / 2) * mapInfo.resolution / VISIBLE_METERS;

  const robotPx = robotPose
    ? worldToPx(mapInfo, robotPose)
    : { x: mapInfo.width / 2, y: mapInfo.height / 2 };

  const theta = robotPose?.theta ?? 0;
  const thetaDeg = theta * (180 / Math.PI);

  const cx = CONTAINER_PX / 2;
  const cy = CONTAINER_PX / 2;

  // Offset of the map image's top-left corner from the wrapper's top-left,
  // chosen so the robot pixel lands exactly at (cx, cy).
  const bx = cx - robotPx.x * scale;
  const by = cy - robotPx.y * scale;

  // Helper: world → wrapper screen coords (same coordinate space as background-image)
  const toScreen = (wx: number, wy: number) => {
    const p = worldToPx(mapInfo, { x: wx, y: wy });
    return { x: bx + p.x * scale, y: by + p.y * scale };
  };

  return (
    <button
      onClick={onClick}
      className="relative rounded-2xl overflow-hidden border border-white/20 shadow-lg cursor-pointer hover:border-accent/60 transition-colors focus:outline-none bg-white dark:bg-black"
      style={{ width: CONTAINER_PX, height: CONTAINER_PX }}
      aria-label="Open navigation map"
    >
      {/*
        Rotating layer: map background image + path + waypoints.
        Both the CSS background and the SVG share the same local coordinate system
        so they cannot drift apart.
      */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transform: `rotate(${thetaDeg - 90}deg)`,
          transformOrigin: `${cx}px ${cy}px`,
        }}
      >
        {/* Map raster — inverted in dark mode */}
        <div
          className="absolute inset-0 dark:invert"
          style={{
            backgroundImage: mapUrl ? `url("${mapUrl}")` : undefined,
            backgroundSize: `${mapInfo.width * scale}px ${mapInfo.height * scale}px`,
            backgroundPosition: `${bx}px ${by}px`,
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Path + waypoints — not inverted */}
        <svg
          style={{ position: 'absolute', inset: 0, width: CONTAINER_PX, height: CONTAINER_PX, overflow: 'visible' }}
        >
          {path.length > 1 && (
            <polyline
              fill="none"
              stroke="#3b82f6"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="6 3"
              points={path.map(([x, y]) => {
                const s = toScreen(x, y);
                return `${s.x},${s.y}`;
              }).join(' ')}
            />
          )}

          {Object.entries(waypoints).map(([name, wp]) => {
            const s = toScreen(wp.x, wp.y);
            const color = wp.dockable ? '#22c55e' : '#3b82f6';
            return (
              <circle key={name} cx={s.x} cy={s.y} r={3} fill={color} stroke="white" strokeWidth={0.8} />
            );
          })}
        </svg>
      </div>

      {/* Robot indicator — fixed at center, always pointing up, not part of rotating layer */}
      <svg
        style={{ position: 'absolute', inset: 0, width: CONTAINER_PX, height: CONTAINER_PX, pointerEvents: 'none' }}
      >
        {robotPose && (
          <g transform={`translate(${cx}, ${cy})`}>
            <circle r={DOT_R * 1.7} fill="rgba(239,68,68,0.18)" />
            <circle r={DOT_R} fill="#ef4444" stroke="white" strokeWidth={DOT_R * 0.27} />
            {/* Arrow pointing up */}
            <polygon
              points={`0,${-DOT_R * 2.2} ${-DOT_R * 0.45},${-DOT_R * 0.6} ${DOT_R * 0.45},${-DOT_R * 0.6}`}
              fill="#ef4444"
              stroke="white"
              strokeWidth={DOT_R * 0.13}
            />
          </g>
        )}
      </svg>

      <div className="absolute inset-0 flex items-end justify-center pb-1.5 pointer-events-none">
        <span className="text-xs text-orange-400 font-bold select-none">tap to expand and pick a waypoint</span>
      </div>
    </button>
  );
}
